import { useState, useCallback, useRef } from 'react';

interface MessageOptions {
  duration?: number; // 表示時間（ミリ秒）
  priority?: boolean; // 優先度の高いメッセージ
  keepAlive?: boolean; // trueの場合、自動で消えない
}

export function useMessage() {
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [messageVisible, setMessageVisible] = useState(false);
  const [keepAlive, setKeepAlive] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const fadeoutRef = useRef<number | null>(null);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (fadeoutRef.current) {
      clearTimeout(fadeoutRef.current);
      fadeoutRef.current = null;
    }
  }, []);

  // メッセージを表示
  const showMessage = useCallback((text: string, options: MessageOptions = {}) => {
    const { duration = 3000, keepAlive: keepAliveOption = false } = options;

    clearAllTimers();

    setCurrentMessage(text);
    setMessageVisible(true);
    setKeepAlive(keepAliveOption);

    // 自動非表示タイマー（keepAliveがfalseの場合のみ）
    if (duration > 0 && !keepAliveOption) {
      timeoutRef.current = setTimeout(() => {
        setMessageVisible(false);
        fadeoutRef.current = setTimeout(() => {
          setCurrentMessage('');
        }, 300) as unknown as number;
      }, duration) as unknown as number;
    }
  }, [clearAllTimers]);

  // メッセージを非表示
  const hideMessage = useCallback(() => {
    clearAllTimers();
    setMessageVisible(false);
    setKeepAlive(false);

    fadeoutRef.current = setTimeout(() => {
      setCurrentMessage('');
    }, 300) as unknown as number;
  }, [clearAllTimers]);

  // ランダムメッセージを表示
  const showRandomMessage = useCallback((messages: string[], options?: MessageOptions) => {
    if (messages.length === 0) return;
    
    const randomIndex = Math.floor(Math.random() * messages.length);
    showMessage(messages[randomIndex], options);
  }, [showMessage]);

  // 重要なメッセージが表示中かどうか
  const hasImportantMessage = useCallback(() => {
    return messageVisible && keepAlive;
  }, [messageVisible, keepAlive]);

  return {
    currentMessage,
    messageVisible,
    keepAlive,
    showMessage,
    hideMessage,
    showRandomMessage,
    hasImportantMessage,
  };
}